import { useEffect, useState } from "react";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { apiClient } from "../api/client";

const RING_SIZE = 10;
const EMA_ALPHA = 0.4;

interface PendingMeta {
  startedAt: number;
}

declare module "axios" {
  export interface InternalAxiosRequestConfig {
    __latencyMeta?: PendingMeta;
  }
}

export type LatencyColor = "green" | "yellow" | "red" | "muted";

export interface ObservedLatency {
  pingMs: number | null;
  samples: number;
  color: LatencyColor;
}

let installed = false;
let currentEma: number | null = null;
let sampleCount = 0;
const ring: number[] = [];
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  });
}

function pushSample(rtt: number): void {
  if (!Number.isFinite(rtt) || rtt < 0) return;
  if (ring.length >= RING_SIZE) ring.shift();
  ring.push(rtt);
  sampleCount += 1;
  currentEma = currentEma == null ? rtt : EMA_ALPHA * rtt + (1 - EMA_ALPHA) * currentEma;
  notify();
}

function colorFor(ms: number | null): LatencyColor {
  if (ms == null) return "muted";
  if (ms < 150) return "green";
  if (ms < 400) return "yellow";
  return "red";
}

function installInterceptors(client: AxiosInstance): void {
  if (installed) return;
  installed = true;

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const startedAt =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    config.__latencyMeta = { startedAt };

    const sentAt = new Date().toISOString();
    const url = (config.url ?? "").toString();
    const isRaceRequest = /\/api\/art-of-the-day\/(race-state|today|status|race\/enter|info)/.test(
      url,
    );

    if (isRaceRequest) {
      // sent as a header so it doesn't bloat the URL (the previous query-param form
      // showed up in logs/proxies for every poll).
      config.headers.set("X-Client-Sent-At", sentAt);
    }
    return config;
  });

  const onSettled = (config?: InternalAxiosRequestConfig | null): void => {
    const meta = config?.__latencyMeta;
    if (!meta) return;
    const now =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    pushSample(now - meta.startedAt);
  };

  client.interceptors.response.use(
    (response) => {
      onSettled(response.config as InternalAxiosRequestConfig);
      return response;
    },
    (error) => {
      onSettled(error?.config as InternalAxiosRequestConfig | undefined);
      return Promise.reject(error);
    },
  );
}

installInterceptors(apiClient);

export function getCurrentObservedLatency(): ObservedLatency {
  return {
    pingMs: currentEma != null ? Math.round(currentEma) : null,
    samples: sampleCount,
    color: colorFor(currentEma != null ? Math.round(currentEma) : null),
  };
}

export function useObservedLatency(): ObservedLatency {
  const [, setTick] = useState(0);

  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }, []);

  return getCurrentObservedLatency();
}
